import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import {
  DEFAULT_TIME_SERIES_DUPLICATE_HANDLING,
  TIME_SERIES_DUPLICATE_HANDLING,
  TimeSeriesDuplicateHandling,
  sumBy,
} from '@anessa/shared-models';
import { I18nPipe } from '../../shared/i18n/i18n-pipe';
import { I18nScopeReadyDirective } from '../../shared/i18n/i18n-scope-ready.directive';
import { provideI18nScope } from '../../shared/i18n/i18n-scope.provider';
import { I18nScopedService } from '../../shared/i18n/i18n-scoped.service';
import { ErrorService } from '../../shared/services/error/error.service';
import { ProjectService } from '../../shared/services/project/project.service';
import { TimeseriesSheetImporterService } from './services/timeseries-sheet-importer.service';
import {
  GetMatchedSheetsResult,
  MultiTabImportService,
  SkippedSheet,
} from './multi-tab-import.service';
import {
  ImportResult,
  SheetParseResult,
} from './schemas/sheet-importer.interface';
import { TimeSeriesTablesService } from '../../features/timeseries-data/services/timeseries-tables.service';

export interface SkippedSheetState {
  selectedTableId: string | null;
  showNewTableInput: boolean;
  newTableName: string;
  resolving: boolean;
}

@Component({
  selector: 'app-multi-tab-import-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    I18nPipe,
    I18nScopeReadyDirective,
  ],
  providers: [
    provideI18nScope('ui/multi-tab-import-dialog'),
    I18nScopedService,
  ],
  templateUrl: './multi-tab-import-dialog.html',
  styleUrl: './multi-tab-import-dialog.scss',
})
export class MultiTabImportDialog implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<MultiTabImportDialog>);
  private readonly projectService = inject(ProjectService);
  private readonly timeseriesTablesService = inject(TimeSeriesTablesService);
  private readonly multiTabImportService = inject(MultiTabImportService);
  private readonly tsSheetImporter = inject(TimeseriesSheetImporterService);
  private readonly errorService = inject(ErrorService);
  readonly i18n = inject(I18nScopedService);

  // ChangeDetectorRef must be injected via constructor for testability and Angular DI context
  // eslint-disable-next-line @angular-eslint/prefer-inject
  constructor(private readonly cdr: ChangeDetectorRef) {}

  readonly duplicateHandlingOptions = TIME_SERIES_DUPLICATE_HANDLING;
  duplicateHandling: TimeSeriesDuplicateHandling =
    DEFAULT_TIME_SERIES_DUPLICATE_HANDLING;

  selectedFile: File | null = null;
  matchResult: GetMatchedSheetsResult | null = null;
  parseResults: SheetParseResult[] | null = null;
  readonly isLoading = signal(false);
  readonly isImporting = signal(false);
  readonly importResults = signal<ImportResult[] | null>(null);

  private readonly skippedSheetStates = new Map<string, SkippedSheetState>();

  get availableTables() {
    return this.timeseriesTablesService.tables();
  }

  get totalValidRows(): number {
    return sumBy(this.parseResults ?? [], (r) => r.validRowCount);
  }

  get totalImported(): number {
    return sumBy(this.importResults() ?? [], (r) => r.created);
  }

  get canImport(): boolean {
    const results = this.parseResults;
    if (!this.matchResult || !results) return false;
    if (this.isLoading() || this.isImporting()) return false;
    if (this.isResolvingAnySkipped) return false;
    if (this.importResults() !== null) return false;
    return this.matchResult.matched.some(
      (_, i) => (results[i]?.validRowCount ?? 0) > 0,
    );
  }

  /** True while any skipped sheet is being created/mapped as a table — blocks
   *  onImport() so it can't run against a matchResult that's still being mutated. */
  get isResolvingAnySkipped(): boolean {
    for (const state of this.skippedSheetStates.values()) {
      if (state.resolving) return true;
    }
    return false;
  }

  ngOnInit(): void {
    // the timeseries table is loaded on project load
    // but following the pattern of defensively loading it here so we can access it in TimeseriesSheetImporterService
    const plantId = this.projectService.currentProject()?.plantMeta?.id;
    if (plantId) {
      this.timeseriesTablesService
        .loadTables(plantId)
        .catch((err: Error) => this.errorService.handle(err));
    }
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    if (!file) {
      this.errorService.handle(new Error('File Selection Failed'), {
        userMessage: this.i18n.t('file_selection_failed'),
      });
      return;
    }

    this.selectedFile = file;
    this.matchResult = null;
    this.parseResults = null;
    this.skippedSheetStates.clear();
    this.isLoading.set(true);

    try {
      // in future if we have multiple importers, we can extend this importers array
      const importers = [this.tsSheetImporter];
      // get all matches from sheet to importer pages, check all sheets in workbook and all importers to match sheet to a section in the app
      this.matchResult = await this.multiTabImportService.getMatchedSheets(
        file,
        importers,
      );
      this.parseResults = await this.multiTabImportService.parseAll(
        file,
        this.matchResult.matched,
      );
    } catch (err) {
      this.errorService.handle(err);
    } finally {
      this.isLoading.set(false);
    }
  }

  getSkippedState(sheetName: string): SkippedSheetState {
    let state = this.skippedSheetStates.get(sheetName);
    if (!state) {
      state = {
        selectedTableId: null,
        showNewTableInput: false,
        newTableName: sheetName,
        resolving: false,
      };
      this.skippedSheetStates.set(sheetName, state);
    }
    return state;
  }

  toggleNewTableInput(sheetName: string): void {
    this.getSkippedState(sheetName).showNewTableInput = true;
  }

  cancelNewTableInput(sheetName: string): void {
    this.getSkippedState(sheetName).showNewTableInput = false;
  }

  async resolveSkippedSheetWithTable(
    skipped: SkippedSheet,
    tableId: string,
  ): Promise<void> {
    const table = this.timeseriesTablesService
      .tables()
      .find((t) => t.id === tableId);
    if (!table) return;

    const state = this.getSkippedState(skipped.name);
    state.resolving = true;
    try {
      const match = this.tsSheetImporter.matchSheetToTable(
        skipped.name,
        skipped.sheetIndex,
        table,
      );
      await this.resolveSkipped(skipped, match);
    } catch (err) {
      this.errorService.handle(err);
    } finally {
      state.resolving = false;
      // matchResult/parseResults/skippedSheetStates are plain mutable state, not
      // signals — this app runs zoneless, so nothing else notifies Angular that
      // these mutations (made here and inside resolveSkipped()) need rendering.
      this.cdr.markForCheck();
    }
  }

  async resolveSkippedSheetAsNewTable(skipped: SkippedSheet): Promise<void> {
    const file = this.selectedFile;
    const currentProject = this.projectService.currentProject();
    const plantId = currentProject?.plantMeta?.id;
    const projectId = currentProject?.id;
    if (!file || !plantId || !projectId) return;

    const state = this.getSkippedState(skipped.name);
    const newTableName = state.newTableName.trim();
    if (!newTableName) return;

    state.resolving = true;
    try {
      const match = await this.tsSheetImporter.createTableForSheet(
        newTableName,
        file,
        skipped.sheetIndex,
        ',',
        plantId,
        projectId,
      );
      await this.resolveSkipped(skipped, match);
    } catch (err) {
      this.errorService.handle(err);
    } finally {
      state.resolving = false;
      // See comment in resolveSkippedSheetWithTable — same zoneless caveat applies here.
      this.cdr.markForCheck();
    }
  }

  /** Parses a newly-resolved skipped sheet and folds it into matchResult.matched/
   *  parseResults so it's treated identically to an auto-matched sheet by
   *  canImport/totalValidRows/onImport. */
  private async resolveSkipped(
    skipped: SkippedSheet,
    match: ReturnType<TimeseriesSheetImporterService['matchSheetToTable']>,
  ): Promise<void> {
    const file = this.selectedFile;
    if (!file || !this.matchResult || !this.parseResults) return;

    const parseResult = await this.tsSheetImporter.parseSheet(file, match, ',');

    this.matchResult.matched.push({ match, importer: this.tsSheetImporter });
    this.parseResults.push(parseResult);
    this.matchResult.skipped = this.matchResult.skipped.filter(
      (s) => s.name !== skipped.name,
    );
    this.skippedSheetStates.delete(skipped.name);
  }

  async onImport(): Promise<void> {
    const file = this.selectedFile;
    if (!this.matchResult || !this.parseResults || !file) return;

    this.isImporting.set(true);
    try {
      const results: ImportResult[] =
        await this.multiTabImportService.importAll(
          this.matchResult.matched,
          this.parseResults,
          { duplicateHandling: this.duplicateHandling },
          file,
          ',',
        );
      // importAll() never throws, per-sheet and per-batch errors are captured in each
      // result. HTTP errors are already surfaced by the api-error interceptor.
      // Keep the dialog open so the user can see the per-sheet results before dismissing.
      this.importResults.set(results);
    } catch (err) {
      this.errorService.handle(err);
    } finally {
      this.isImporting.set(false);
    }
  }

  onDone(): void {
    this.dialogRef.close(this.importResults());
  }
}
